import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  Chip,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNotification } from './context/NotificationContext';

const API_BASE_URL = 'https://frek.cc/proxy/38.54.86.24:5003/api';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0c0d12',
      paper: '#14171f',
    },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: {
      main: '#6aa9ff',
    },
    success: {
      main: '#5ad27a',
    },
    error: {
      main: '#ff6b6b',
    },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const formatDate = (isoString) => {
  if (!isoString) return 'N/A';
  return new Date(isoString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return 'N/A';
  return `PHP ${Number(amount).toFixed(2)}`;
};

const NotificationRow = ({ notification, onProcess, onDelete, onToggleSelect, isSelected, rowBusy }) => {
  const isProcessed = notification.status === 'processed';

  return (
    <TableRow
      hover
      sx={{
        '& td': { borderColor: 'rgba(255,255,255,0.08)' },
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={(event) => onToggleSelect(notification._id, event.target.checked)}
          disabled={rowBusy}
        />
      </TableCell>
      <TableCell sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>
        {formatDate(notification.received_at)}
      </TableCell>
      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{notification.app || 'N/A'}</TableCell>
      <TableCell sx={{ minWidth: 120 }}>{notification.title || 'N/A'}</TableCell>
      <TableCell sx={{ minWidth: 280, maxWidth: 420, whiteSpace: 'normal', color: 'text.secondary' }}>
        {notification.text || 'N/A'}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{notification.sender || 'N/A'}</TableCell>
      <TableCell sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}>
        {formatAmount(notification.amount)}
      </TableCell>
      <TableCell>
        <Chip
          label={notification.status || 'unknown'}
          color={isProcessed ? 'success' : 'error'}
          size="small"
          sx={{
            textTransform: 'capitalize',
            fontWeight: 600,
            border: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: isProcessed ? 'rgba(90,210,122,0.18)' : 'rgba(255,107,107,0.16)',
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {!isProcessed && (
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={rowBusy}
              onClick={() => onProcess(notification._id)}
            >
              Process
            </Button>
          )}
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={rowBusy}
            onClick={() => onDelete(notification._id)}
          >
            Delete
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const NotificationReceived = () => {
  const { showNotification } = useNotification();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [busyIds, setBusyIds] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetIds, setDeleteTargetIds] = useState([]);

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/notifications`);
      if (!response.ok) {
        throw new Error(`Network response was not ok. Status: ${response.status}`);
      }
      const data = await response.json();
      setNotifications(Array.isArray(data) ? data : []);
      setSelectedIds((prev) => prev.filter((id) => (Array.isArray(data) ? data : []).some((item) => item._id === id)));
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
      setError('Could not connect to the server. Check backend status and try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(false);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const intervalId = setInterval(() => {
      fetchNotifications(true);
    }, 30000);

    return () => clearInterval(intervalId);
  }, [autoRefresh, fetchNotifications]);

  const summary = useMemo(() => {
    const total = notifications.length;
    const processed = notifications.filter((n) => n.status === 'processed').length;
    const pending = total - processed;
    const amountTotal = notifications.reduce((acc, item) => {
      const value = Number(item.amount);
      return Number.isNaN(value) ? acc : acc + value;
    }, 0);

    return { total, processed, pending, amountTotal };
  }, [notifications]);

  const setBusy = (id, value) => {
    setBusyIds((prev) => ({ ...prev, [id]: value }));
  };

  const selectedCount = selectedIds.length;
  const allSelected = notifications.length > 0 && selectedCount === notifications.length;
  const someSelected = selectedCount > 0 && selectedCount < notifications.length;

  const handleToggleSelect = (id, checked) => {
    setSelectedIds((prev) => (
      checked ? Array.from(new Set([...prev, id])) : prev.filter((item) => item !== id)
    ));
  };

  const handleToggleSelectAll = (checked) => {
    setSelectedIds(checked ? notifications.map((item) => item._id) : []);
  };

  const handleProcess = async (id) => {
    setBusy(id, true);
    try {
      const response = await fetch(`${API_BASE_URL}/notifications/process/${id}`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to process notification');
      showNotification('Notification processed.', 'success');
      fetchNotifications(true);
    } catch (e) {
      console.error('Error processing notification:', e);
      showNotification('Failed to process notification.', 'error');
    } finally {
      setBusy(id, false);
    }
  };

  const openDeleteDialog = (ids) => {
    if (!ids.length) return;
    setDeleteTargetIds(ids);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetIds([]);
  };

  const handleDelete = async () => {
    const ids = deleteTargetIds;
    if (!ids.length) return;

    ids.forEach((id) => setBusy(id, true));
    try {
      const results = await Promise.all(ids.map(async (id) => {
        const response = await fetch(`${API_BASE_URL}/notifications/delete/${id}`, { method: 'POST' });
        if (!response.ok) {
          throw new Error(`Failed to delete notification ${id}`);
        }
        return id;
      }));

      setNotifications((prev) => prev.filter((n) => !results.includes(n._id)));
      setSelectedIds((prev) => prev.filter((id) => !results.includes(id)));
      showNotification(
        results.length === 1 ? 'Notification deleted.' : `${results.length} notifications deleted.`,
        'success'
      );
      closeDeleteDialog();
    } catch (e) {
      console.error('Error deleting notification:', e);
      showNotification('Failed to delete notification.', 'error');
    } finally {
      ids.forEach((id) => setBusy(id, false));
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Notification Received
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                      Real-time incoming notification monitor.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {selectedCount > 0 && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => openDeleteDialog(selectedIds)}
                        disabled={selectedIds.some((id) => Boolean(busyIds[id]))}
                      >
                        Delete Selected ({selectedCount})
                      </Button>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoRefresh}
                          onChange={(e) => setAutoRefresh(e.target.checked)}
                          color="primary"
                          sx={{ transform: 'scale(0.9)' }}
                        />
                      }
                      label={<Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>Auto Refresh</Typography>}
                      sx={{ m: 0 }}
                    />
                    <Tooltip title="Refresh now">
                      <span>
                        <Button
                          startIcon={<RefreshIcon />}
                          variant="contained"
                          size="small"
                          onClick={() => fetchNotifications(false)}
                          disabled={loading}
                        >
                          Refresh
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Total ${summary.total}`} sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'text.primary', fontWeight: 700 }} />
                  <Chip label={`Pending ${summary.pending}`} sx={{ bgcolor: 'rgba(255,107,107,0.18)', color: '#ffb3b3', fontWeight: 700 }} />
                  <Chip label={`Processed ${summary.processed}`} sx={{ bgcolor: 'rgba(90,210,122,0.18)', color: '#b9f2c8', fontWeight: 700 }} />
                  <Chip label={`Amount PHP ${summary.amountTotal.toFixed(2)}`} sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }} />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              {loading && notifications.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box sx={{ p: 2 }}>
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={() => fetchNotifications(false)}>
                        Retry
                      </Button>
                    }
                  >
                    {error}
                  </Alert>
                </Box>
              ) : notifications.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.55)' }}>
                  No notifications found.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: '72vh' }}>
                  <Table stickyHeader size="small" aria-label="notifications table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          padding="checkbox"
                          sx={{
                            backgroundColor: 'rgba(30, 35, 52, 0.92)',
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                          }}
                        >
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(event) => handleToggleSelectAll(event.target.checked)}
                          />
                        </TableCell>
                        {['Received At', 'App', 'Title', 'Text', 'Sender', 'Amount', 'Status', 'Actions'].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontWeight: 700,
                              color: '#d9e6ff',
                              backgroundColor: 'rgba(30, 35, 52, 0.92)',
                              borderBottom: '1px solid rgba(255,255,255,0.12)',
                              whiteSpace: 'nowrap',
                            }}
                            align={header === 'Actions' ? 'right' : 'left'}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notifications.map((notification) => (
                        <NotificationRow
                          key={notification._id}
                          notification={notification}
                          onProcess={handleProcess}
                          onDelete={(id) => openDeleteDialog([id])}
                          onToggleSelect={handleToggleSelect}
                          isSelected={selectedIds.includes(notification._id)}
                          rowBusy={Boolean(busyIds[notification._id])}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Stack>
        </Container>

        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
          <DialogTitle>Delete Notifications</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteTargetIds.length === 1
                ? 'Are you sure you want to delete this notification?'
                : `Are you sure you want to delete ${deleteTargetIds.length} notifications?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default NotificationReceived;
